/* eslint-disable @typescript-eslint/no-use-before-define */
import { ImageCache } from './ImageCache';
import { Log } from '../../AuxillaryService/Log';
import { ActionQueue, Priority } from '../../Utility/ActionQueue';
import { uniqueId } from '../../Utility';
import { isCachedUrl, shouldBeIgnored } from './Miscellaneous';
import * as shortCircuit from './ShortCircuit';
import { debugMutationEventCount } from './MutationObserver';
import {
    ImageCacheInvalidUriError,
    ImageCacheShortCircuitError,
    ImageCacheCachedError,
} from './Errors';

const cssClassBase = 'image-asset';
const cssClass = {
    base: cssClassBase,
    found: `${cssClassBase}--found`,
    inProgress: `${cssClassBase}--caching`,
    finished: `${cssClassBase}--cached`,
    failed: `${cssClassBase}--failed`,
};

/**
 * Find and Replace Images with Cached URLs
 */
export function findAndReplaceImages(target: HTMLElement): void {
    const elements = findImages(target);

    // Update Elements
    for (let idx = 0; idx < elements.length; idx++) {
        replaceImage(elements[idx]);
    }
}

/**
 * Find image elements that may need caching
 */
function findImages(target: HTMLElement): HTMLElement[] {
    const imageElements: HTMLElement[] = [];
    const selector = `img:not(.${cssClass.base}):not([src^="data:"]):not([src^="blob:"]):not([src^="reachasset:"])`;
    const elements = target.querySelectorAll<HTMLElement>(selector);

    // Check Element, ignore if the target has or is a child of an element with
    // the class .image-cache-ignore
    if (!shouldBeIgnored(target)) {
        if (target.matches(selector)) {
            imageElements.push(target);
        }
        // Check Element's Children
        for (let idx = 0; idx < elements.length; idx++) {
            //Ignore if the element has a parent with the class
            //.image-cache-ignore
            if (!shouldBeIgnored(elements[idx])) {
                imageElements.push(elements[idx]);
            }
        }
    }

    Array.from(imageElements).forEach((elm) => {
        elm.classList.add(cssClass.base);
        elm.classList.add(cssClass.found);
    });

    return imageElements;
}

/**
 * Try to replace image element source with cached image
 */
function replaceImage(element: HTMLElement, src?: string): void {
    ActionQueue.New()
        .Named(`ImageCache_${uniqueId()}`)
        .OnExecuteCall(() => {
            try {
                _replaceImage(element, src);
            } catch (err) {
                if (
                    err instanceof ImageCacheInvalidUriError ||
                    err instanceof ImageCacheCachedError
                ) {
                    // Code is working as intended. This is not a problem.
                    Log.Debug(String(err), null, __fileline, {
                        element,
                        src,
                        assetUri: err.detail.assetUri,
                    });
                } else if (err instanceof ImageCacheShortCircuitError) {
                    // Code is working as intended, but the retry attempts have exceeded the allowed
                    // amount. This could indicate a problem.
                    Log.Warning(String(err), null, __fileline, {
                        element,
                        src,
                        assetUri: err.detail.assetUri,
                    });
                } else {
                    // Unknown error. This is definitely a problem.
                    Log.Error(String(err), null, __fileline, { element, src });
                }
            }
        })
        .WithPriority(Priority.LOW)
        .HasComplexity(1500)
        .Commit();
}

/**
 * @see {@link replaceImage}
 * @throws {@link ImageCacheInvalidUriError} when attempting to cache a URI that goes nowhere.
 * @throws {@link ImageCacheCachedError} when the image has already been cached and the DOM has been
 * updated.
 * @throws {@link ImageCacheShortCircuitError} when the cache attempts have failed too many times.
 */
function _replaceImage(element: HTMLElement, src?: string): void {
    const url = src || element.dataset.src || element.getAttribute('src') || '';
    const retryAttemptsExceeded = shortCircuit.hasHitMaxAttempts(url);

    if (url == '') {
        throw new ImageCacheInvalidUriError('Unable to cache empty URI.', url);
    }

    if (url.startsWith('#') || url.endsWith('#')) {
        throw new ImageCacheInvalidUriError('Unable to cache URI fragment.', url);
    }

    if (isCachedUrl(url) || element.classList.contains(cssClass.finished)) {
        throw new ImageCacheCachedError('Image has already been cached.', url);
    }

    if (retryAttemptsExceeded) {
        throw new ImageCacheShortCircuitError(
            'The image has reached its max retry count and no further download attempts will be made.',
            url
        );
    }

    element.classList.add(cssClass.inProgress);
    element.classList.remove(cssClass.found);

    const cachedImagePromise = ImageCache.getCached(url);

    cachedImagePromise.done(function (cachedURL: string) {
        if (element.dataset.cacheUrl === url) {
            // this image has already been cached and replaced, so don't do
            // anything
            return;
        }

        element.dataset.cacheUrl = url; // debugging
        // update image src
        element.setAttribute('src', cachedURL);
        element.classList.add(cssClass.finished);
        $(element).show();
    });

    cachedImagePromise.fail(function (
        xhr: XMLHttpRequest,
        statusText: string,
        msg: string,
        event: Event
    ) {
        Log.Warning(
            'Failed to get cached image: ' + url,
            null,
            __fileline,
            ImageCache.createPromiseFailObject(xhr, statusText, msg, event)
        );

        element.classList.add(cssClass.failed);

        retryReplaceImage(element, url);
    });

    cachedImagePromise.always(function () {
        element.classList.remove(cssClass.inProgress);
    });
}

/**
 * Retry to replace image element source with cached image as long as we haven't
 * retried too many times
 */
function retryReplaceImage(element: HTMLElement, src?: string): void {
    let retryCount = parseInt(element.dataset.errorRetryCount || '0') || 0;

    retryCount++;

    element.dataset.errorRetryCount = String(retryCount);
    const retryDelay = 1000 * retryCount;

    if (retryCount < 50) {
        Log.Warning(
            `Retry retrieving cached image in ${Math.floor(retryDelay / 1000)} seconds.`,
            null,
            __fileline,
            { element: element }
        );

        element.classList.add(cssClass.found);
        element.classList.remove(cssClass.failed);

        setTimeout(function () {
            replaceImage(element, src);
        }, retryDelay);
    }
}

/**
 * Display debugging information about the current status about the image assets
 * found by {@link ImageCache}.
 */
export function cachingImageAssetsDebug(): void {
    console.table({
        totalImages: document.querySelectorAll(`.${cssClass.base}`).length,
        foundImages: document.querySelectorAll(`.${cssClass.found}`).length,
        inProgressImages: document.querySelectorAll(`.${cssClass.inProgress}`).length,
        finishedImages: document.querySelectorAll(`.${cssClass.finished}`).length,
        failedImages: document.querySelectorAll(`.${cssClass.failed}`).length,
        mutationEvents: debugMutationEventCount,
    });
}
